import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface TiebreakDecisionView {
  criterion: string;
  label: string;
  ownValue: number | null;
  rivalValue: number | null;
  rivalNames: string[];
}

interface Props {
  decision: TiebreakDecisionView;
  name: string;
  testId?: string;
}

interface BubblePosition {
  top: number;
  left: number;
}

const BUBBLE_GAP = 6;
const BUBBLE_MAX_WIDTH = 240;
const VIEWPORT_MARGIN = 8;

// Bez tego wiersz wygląda, jakby przeskoczył sąsiada bez powodu — a przy równym
// dorobku to właśnie jest pytanie, które zadaje każdy, kto patrzy na tabelę.
export function tiebreakWinReason(decision: TiebreakDecisionView, name: string): string {
  const rivals = decision.rivalNames.join(', ');
  const over = rivals.length === 0 ? 'niżej stojących' : rivals;

  if (decision.ownValue === null || decision.rivalValue === null) {
    return `${name} stoi nad ${over}, bo wygrał(a) z nimi mecz bezpośredni.`;
  }

  return `${name} stoi nad ${over} na kryterium „${decision.label}": ${decision.ownValue} do ${decision.rivalValue}.`;
}

export function placeBubble(markRect: DOMRect, viewportWidth: number): BubblePosition {
  const rightmostLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - BUBBLE_MAX_WIDTH - VIEWPORT_MARGIN);

  return {
    top: markRect.bottom + BUBBLE_GAP,
    left: Math.min(markRect.left, rightmostLeft),
  };
}

// Dymek wisi w portalu na `position: fixed` — tabela siedzi w `overflow-x: auto`,
// a absolutnie pozycjonowany potomek poszerzałby jej obszar przewijania.
export const TiebreakWinMark: React.FC<Props> = ({ decision, name, testId = 'tiebreak-win' }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<BubblePosition | null>(null);
  const markRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const reason = tiebreakWinReason(decision, name);

  const reposition = useCallback(() => {
    const mark = markRef.current;

    if (!mark) {
      return;
    }

    setPosition(placeBubble(mark.getBoundingClientRect(), window.innerWidth));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: MouseEvent): void {
      const target = event.target as Node;

      if (markRef.current?.contains(target) || bubbleRef.current?.contains(target)) {
        return;
      }

      setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  return (
    <span className="tiebreak-win">
      <button
        type="button"
        ref={markRef}
        className="tiebreak-win__mark"
        data-testid={`${testId}-mark`}
        aria-label={`Dlaczego wyżej — ${name}`}
        aria-expanded={open}
        title={reason}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        !
      </button>
      {open &&
        createPortal(
          <span
            ref={bubbleRef}
            className="tiebreak-win__bubble"
            role="tooltip"
            data-testid={`${testId}-reason`}
            style={{
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {reason}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default TiebreakWinMark;
